/**
 * The Warrior (PRD #792), the one class this PRD declares.
 *
 * Every number here is the number the class shipped with. The curves are the
 * only new content, and both evaluate at level 1 to the flat constant they
 * replace: 120 health and 40 mana. That is the Warrior regression stated as
 * data rather than as a promise, and a test pins it.
 *
 * The kit is the shipped kit in the shipped order. PRD #713 lists Warcry as
 * the fifth ability and Piercing Thrust as the basic; the shipped Warrior has
 * Aegis fifth and Crescent Sweep as its basic, and PRD #792 keeps what works
 * and updates the document rather than rewriting working abilities to match it.
 * All five unlock at level 1, which is also what shipped: this PRD builds the
 * unlock machinery and the guard that proves an unlock level is reachable, and
 * the staggering of a kit across a campaign is authored with the two classes
 * that follow in PRD #800.
 */

import type { ArpgClassDef } from './types';

/**
 * The mana regeneration the dungeon has always run: one point every five
 * simulation ticks. It is a class curve now rather than a constant inside the
 * dungeon adapter, which is what lets Wisdom and a future affix move it.
 */
const SHIPPED_MANA_REGEN_PER_TICK = 1 / 5;

export const WARRIOR_CLASS: ArpgClassDef = {
  id: 'warrior',
  name: 'Warrior',
  blurb: 'A durable melee Hero with a halberd and plate armor.',
  playable: true,
  weapons: {
    halberd: { damage: 20, grip: 'two-handed' },
    'sword.britannian': { damage: 24, grip: 'one-handed' },
  },
  // The armour ids the Warrior's three families paint (PRD #795). Ringmail,
  // chainmail, and plate all draw on the shipped plate layers and are told
  // apart by their material hue, which is what the ladder was costed on.
  allowedEquipmentIds: new Set([
    'plate.chest',
    'plate.arms',
    'plate.legs',
    'boots.plain',
    'shoes.plain',
    'halberd',
    'sword.britannian',
  ]),
  curves: {
    // 120 at level 1, 310 at the cap: deeper content is survivable without the
    // level 1 Warrior changing at all.
    maxHealth: { base: 120, perLevel: 10 },
    maxMana: { base: 40, perLevel: 2 },
    // The dungeon pauses health regeneration for a Hero in a run. Vitality is
    // what turns this on, which is the point of spending in it.
    healthRegenPerTick: 0,
    manaRegenPerTick: SHIPPED_MANA_REGEN_PER_TICK,
    // A Warrior crits only once Dexterity is spent, which is what keeps the
    // level 1 damage numbers exactly where they were.
    critChance: 0,
    critMultiplier: 1.5,
  },
  // The slots are the bindings the Warrior shipped with, written down rather
  // than hand-built into the action bar (PRD #800). Nothing about how the
  // shipped class plays moves.
  abilities: [
    { abilityId: 'warrior-auto-attack', unlockLevel: 1, slot: 'right-mouse' },
    { abilityId: 'warrior-whirlwind', unlockLevel: 1, slot: 'key-3' },
    { abilityId: 'warrior-charge', unlockLevel: 1, slot: 'key-4' },
    { abilityId: 'warrior-piercing-thrust', unlockLevel: 1, slot: 'left-mouse' },
    { abilityId: 'warrior-aegis', unlockLevel: 1, slot: 'key-5' },
  ],
  startingEquipment: {
    mainHandId: 'halberd',
    equipped: [
      { id: 'plate.chest', layer: 'armor-chest' },
      { id: 'plate.arms', layer: 'armor-arms' },
      { id: 'plate.legs', layer: 'armor-legs' },
      { id: 'boots.plain', layer: 'feet', hue: 1109 },
      { id: 'halberd', layer: 'weapon' },
    ],
  },
  balanceFloor: {
    // Crescent Sweep deals 25 with a halberd and recovers in ten ticks, so the
    // 70-health placeholder dies on the third swing, twenty-one ticks in.
    maxTicksToKillPlaceholder: 40,
    // 120 health against a twelve-damage swing is ten hits of margin.
    minPlaceholderSwingsSurvived: 8,
    // Aegis is the costliest thing the kit casts, at ten of a forty pool.
    minCostliestCastsFromFullMana: 3,
  },
};
